<script lang="ts">
    import { onMount } from "svelte";
    import { fly } from "svelte/transition";
    import {
        currentFrameDetection,
        currentSegmentIdx,
        selectedVerticalLine,
    } from "../stores/segment";
    import * as THREE from "three";
    import { Camera } from "../utils/camera";
    // import { blockPoints } from "../constants/blockpoints";
    import { segments } from "../constants/segments";
    import { seeking } from "../stores/player";
    import type { MeshBasicMaterial } from "three";

    const aspectRatio = 9 / 16;

    let canvas: HTMLCanvasElement;
    let canvasBoundingClientRect: DOMRect;
    let renderer: THREE.WebGLRenderer;
    const scene = new THREE.Scene();
    let vYardIntersect: THREE.Mesh;
    const camera = new Camera();
    const mouse = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();
    const vYardLines: THREE.Mesh[] = [];
    let vYardBet: number | null = null;
    let yardsTextMesh: { [yds: number]: THREE.Mesh } = {};

    const colors = {
        bet: new THREE.Color(0x32cd32),
        line: new THREE.Color(0x32cd32),
        hover: new THREE.Color(0xffffff),
        default: new THREE.Color(0x00ff00),
    };

    onMount(async () => {
        renderer = new THREE.WebGLRenderer({ canvas, alpha: true });

        resizeWindow();

        // Block points
        // const geo = new THREE.BoxGeometry(60 / 4, 74 / 4, 0);
        // for (const bp of blockPoints) {
        //     const material = new THREE.MeshBasicMaterial({
        //         color: 0x00ff00,
        //         wireframe: true,
        //         opacity: 1,
        //     });
        //     const p = new THREE.Mesh(geo, material);
        //     p.position.x = bp[0];
        //     p.position.y = bp[1];
        //     scene.add(p);
        // }

        const loader = new THREE.FontLoader();
        loader.load("./fonts/Montserrat_Bold.json", (font) => {
            yardsTextMesh = Object.fromEntries(
                [
                    1.5,
                    2.5,
                    3.5,
                    4.5,
                    5.5,
                    6.5,
                    7.5,
                    8.5,
                    9.5,
                    10.5,
                    11.5,
                    12.5,
                    13.5,
                    14.5,
                ].map((y) => {
                    const text = new THREE.TextGeometry(`${y}`, {
                        font: font,
                        size: 25,
                        height: 0.1,
                        curveSegments: 12,
                        bevelEnabled: true,
                        bevelThickness: 1,
                        bevelSize: 1,
                        bevelOffset: 0,
                        bevelSegments: 5,
                    });
                    const textGeo = new THREE.BufferGeometry().fromGeometry(
                        text
                    );
                    const mat = new THREE.MeshPhongMaterial({
                        color: 0xffffff,
                        flatShading: true,
                        visible: false,
                    });
                    const mesh = new THREE.Mesh(textGeo, mat);
                    mesh.position.x = y * 100;
                    mesh.position.y = 320;
                    mesh.rotation.y = -Math.PI;
                    mesh.rotation.z = -Math.PI;
                    mesh.layers.enable(2);
                    scene.add(mesh);
                    return [y, mesh];
                })
            );
        });

        // const dirLight = new THREE.DirectionalLight(0xffffff, 0.125);
        // dirLight.position.set(700, 1400, -300).normalize();
        // scene.add(dirLight);
        const pointLight = new THREE.PointLight(0xffffff, 1.5);
        pointLight.position.set(700, 320, -1000);
        scene.add(pointLight);
        renderer.render(scene, camera);

        raycaster.layers.set(1);

        window.addEventListener("resize", resizeWindow);
        document.addEventListener("mousemove", onMouseEvent, false);
        document.addEventListener("click", onMouseEvent, false);

        return () => {
            window.removeEventListener("resize", resizeWindow);
            document.removeEventListener("mousemove", onMouseEvent);
            document.addEventListener("click", onMouseEvent, false);
        };
    });

    const resizeWindow = (): void => {
        if (!renderer) return;

        let width = document.body.clientWidth;
        let height = document.body.clientHeight;

        if (width * aspectRatio < height) {
            renderer.setSize(width, width * aspectRatio);
        } else {
            renderer.setSize(height / aspectRatio, height);
        }
        canvasBoundingClientRect = canvas.getBoundingClientRect();
    };

    const reset = (): void => {
        vYardBet = null;
        for (const vYard of vYardLines) {
            (vYard.material as THREE.MeshBasicMaterial).color = colors.line;
        }
        if (
            $currentFrameDetection &&
            $currentFrameDetection.timestamp <
                segments[$currentSegmentIdx].startPlay * 1000
        ) {
            for (const vYard of vYardLines) {
                (vYard.material as THREE.Material).visible = true;
            }
        }
        if (renderer && camera) {
            renderer.render(scene, camera);
        }
    };

    $: {
        if ($currentFrameDetection !== undefined && camera && renderer) {
            camera.update(
                $currentFrameDetection.calibration.projection_matrix,
                $currentFrameDetection.calibration.inverted_view_matrix
            );
            if (
                $currentFrameDetection.timestamp >=
                segments[$currentSegmentIdx].startPlay * 1000
            ) {
                for (const vYard of vYardLines) {
                    if (vYard.position.x !== vYardBet)
                        (vYard.material as THREE.Material).visible = false;
                }
                for (const vYardText of Object.values(yardsTextMesh)) {
                    (vYardText.material as THREE.Material).visible = false;
                }
                $selectedVerticalLine = null;
            }
            renderer.render(scene, camera);
        }
    }

    $: {
        for (const vYard of vYardLines) {
            scene.remove(vYard);
        }
        // Vertical Lines
        const geoYard = new THREE.BoxGeometry(11, 640, 0);
        vYardIntersect = null;
        for (const posY of Object.keys(
            segments[$currentSegmentIdx].progress_odds
        )) {
            const material = new THREE.MeshBasicMaterial({
                color: colors.line,
                opacity: 0.08,
                transparent: true,
            });
            const vYard = new THREE.Mesh(geoYard, material);
            vYard.position.x = parseInt(posY);
            vYard.position.y = 320;
            vYard.name = "betLine";
            vYard.layers.enable(1);
            vYardLines.push(vYard);
            scene.add(vYard);
        }
    }

    $: if ($seeking) reset();

    const onMouseEvent = (event: MouseEvent): void => {
        if (!canvas) return;
        if (!camera) return;
        if (!canvasBoundingClientRect) return;
        if (
            $currentFrameDetection &&
            $currentFrameDetection.timestamp >=
                segments[$currentSegmentIdx].startPlay * 1000
        ) {
            document.body.style.cursor = "default";
            return;
        }
        const newMouse = new THREE.Vector2();
        newMouse.x = event.clientX;
        newMouse.y = event.clientY;

        const normMouse = new THREE.Vector2(
            ((newMouse.x - canvasBoundingClientRect.x) /
                canvasBoundingClientRect.width) *
                2 -
                1,
            (-(newMouse.y - canvasBoundingClientRect.y) /
                canvasBoundingClientRect.height) *
                2 +
                1
        );

        raycaster.setFromCamera(normMouse, camera);

        const intersects = raycaster.intersectObjects(scene.children);
        if (intersects.length > 0) {
            const i = intersects[0].object as THREE.Mesh;

            if (vYardIntersect && i !== vYardIntersect) {
                if (vYardIntersect.position.x === vYardBet)
                    (vYardIntersect.material as MeshBasicMaterial).color =
                        colors.bet;
                else
                    (vYardIntersect.material as MeshBasicMaterial).color =
                        colors.line;
                const previous_yardage =
                    segments[$currentSegmentIdx].progress_odds[
                        vYardIntersect.position.x
                    ].yardage;
                (yardsTextMesh[previous_yardage]
                    .material as THREE.Material).visible = false;
            }
            vYardIntersect = i;

            const yardage =
                segments[$currentSegmentIdx].progress_odds[i.position.x]
                    .yardage;
            if (event.type === "click") {
                if ($selectedVerticalLine === null) {
                    $selectedVerticalLine = vYardIntersect.position.x;
                    mouse.copy(newMouse);
                    (yardsTextMesh[yardage]
                        .material as THREE.Material).visible = false;
                } else {
                    $selectedVerticalLine = null;
                }
            } else {
                if (
                    $selectedVerticalLine === null &&
                    yardage in yardsTextMesh
                ) {
                    (vYardIntersect.material as MeshBasicMaterial).color =
                        colors.hover;
                    yardsTextMesh[yardage].position.x =
                        vYardIntersect.position.x;
                    yardsTextMesh[yardage].position.y = intersects[0].point.y;
                    (yardsTextMesh[yardage]
                        .material as THREE.Material).visible = true;
                }
            }
            document.body.style.cursor = "pointer";

            renderer.render(scene, camera);
        } else {
            if (event.type === "click") {
                $selectedVerticalLine = null;
            }
            if (vYardIntersect) {
                if (vYardIntersect.position.x === vYardBet)
                    (vYardIntersect.material as MeshBasicMaterial).color =
                        colors.bet;
                else
                    (vYardIntersect.material as MeshBasicMaterial).color =
                        colors.line;
                document.body.style.cursor = "default";
                const previous_yardage =
                    segments[$currentSegmentIdx].progress_odds[
                        vYardIntersect.position.x
                    ].yardage;
                (yardsTextMesh[previous_yardage]
                    .material as THREE.Material).visible = false;
                vYardIntersect = null;
                renderer.render(scene, camera);
            }
        }
        event.preventDefault();
    };

    const onBetClick = (event: MouseEvent): void => {
        vYardBet = $selectedVerticalLine;
        for (const vYard of vYardLines) {
            if (vYard.position.x === vYardBet) {
                (vYard.material as MeshBasicMaterial).opacity = 0.2;
            } else {
                (vYard.material as MeshBasicMaterial).opacity = 0.08;
            }
        }
        $selectedVerticalLine = null;
        event.stopPropagation();
    };

    const tooltipStyle = (): string => {
        const ts = $currentFrameDetection?.timestamp;
        if (mouse === undefined) return "--tooltip-visible: hidden;";
        if ($selectedVerticalLine === null) return "--tooltip-visible: hidden;";
        if (
            ts === undefined ||
            ts >= segments[$currentSegmentIdx].startPlay * 1000
        )
            return "--tooltip-visible: hidden;";
        let innerWidth = document.documentElement.clientWidth;
        let left = Math.min(innerWidth - 140, Math.max(140, mouse.x));
        let top = Math.max(mouse.y, 80);
        return `--tooltip-visible: visible; --tooltip-left: ${left}px; --tooltip-top: ${top}px;`;
    };
</script>

<style>
    .container {
        position: absolute;
        margin: 0 auto;
        top: 50%;
        transform: translate(0, -50%);
        max-width: none;
    }

    .tooltip {
        border-radius: 4px;
        background-color: #ffffff;
        width: 280px;
        height: 60px;
        left: var(--tooltip-left);
        top: var(--tooltip-top);
        position: absolute;
        transform: translate(-50%, calc(-100% - 10px));
        margin-bottom: 5px;
        padding: 2px;
        box-shadow: 3px 5px 18px #000000;
        font-family: "Montserrat", sans-serif;
        letter-spacing: 0.5px;
        display: flex;
        align-items: center;
        z-index: 30;
        user-select: none;
        justify-content: space-between;
        visibility: var(--tooltip-visible);
        box-shadow: 3px 5px 18px #000000;
    }

    .tooltip-button {
        border: 2px solid #eaeaea;
        background: linear-gradient(#f8f8f8, #f0f0f0);
        color: black;
        font-size: small;
        font-weight: bold;
        height: 60%;
        border-radius: 4px;
        text-align: center;
        -webkit-transition-duration: 0.4s; /* Safari */
        transition-duration: 0.4s;
        text-decoration: none;
        overflow: hidden;
        cursor: pointer;
        font-family: "Montserrat", sans-serif;
        letter-spacing: 0.5px;
    }

    .tooltip-sub {
        display: table;
        margin: 8px;
        width: 100%;
    }

    .tooltip-header {
        width: 100%;
        font-weight: bolder;
        /* display: table-row; */
        display: inline-flex;
        justify-content: space-between;
    }

    .tooltip-header-yardage {
        font-weight: bold;
        color: #000000;
        font-size: small;
        margin-top: auto;
    }

    .tooltip-header-gained {
        color: #888888;
        font-size: x-small;
        margin-top: auto;
    }
    .tooltip-buttons {
        font-weight: bolder;
        color: #000000;
        font-size: small;
        font-weight: bold;
        display: table-row;
        text-transform: uppercase;
        width: 100%;
        display: inline-flex;
        justify-content: space-between;
    }

    .tooltip-button {
        font-weight: bolder;
        color: #000000;
        font-size: small;
        font-weight: bold;
        text-transform: uppercase;
        width: 45%;
        border: 2px solid #86b122;
        background: linear-gradient(#abd840, #86b11d);
        color: white;
        justify-content: space-between;
        display: flex;
    }

    .tooltip-button > div {
        display: inline-flex;
    }
</style>

<div class="container" style="max-width: none;">
    <canvas bind:this={canvas} />
</div>

{#key $selectedVerticalLine}
    <div
        transition:fly={{ y: 10, duration: 500 }}
        class="tooltip"
        style={tooltipStyle()}>
        <div class="tooltip-sub">
            <div class="player-info-sub">
                <div class="tooltip-header">
                    <div class="tooltip-header-yardage">
                        {segments[$currentSegmentIdx].progress_odds[$selectedVerticalLine]?.yardage}YDS
                    </div>
                    <div class="tooltip-header-gained">Gained Next Play</div>
                </div>
                <div class="tooltip-buttons">
                    <button class="tooltip-button" on:click={onBetClick}>
                        <div>UNDER</div>
                        <div>
                            {segments[$currentSegmentIdx].progress_odds && $selectedVerticalLine ? segments[$currentSegmentIdx].progress_odds[$selectedVerticalLine].under : undefined}
                        </div>
                    </button>
                    <button class="tooltip-button" on:click={onBetClick}>
                        <div>OVER</div>
                        <div>
                            {segments[$currentSegmentIdx].progress_odds && $selectedVerticalLine ? segments[$currentSegmentIdx].progress_odds[$selectedVerticalLine].over : undefined}
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </div>
{/key}
